import {gsap, TimelineLite} from "gsap";
import {JumpingWildAnimStates, JumpinWildSkins, JW_TYPE, SYMBOL_NAME} from "./enums/RoadKillEnums";
import {POSITION} from "./interfaces/RoadKillInterfaces";
import {RoadKillUtils} from "../utils/RoadKillUtils";
import {ISpecialSymData} from "../server/RoadKillData";
import {Multiplier} from "./Multiplier";
import {RoadKillGameAssets} from "../RoadKillGameAssets";
import {RoadKill} from "../RoadKill";
import {RoadKillGameMode} from "../server/RoadKillGameMode";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {RKHelper} from "../utils/RKHelper";
import {RoadKillSound} from "../sound/RoadKillSound";
import {TopAnimalHead} from "./TopAnimalHead";


export class JumpingWild extends PIXI.spine.Spine {
    private readonly _type: JW_TYPE;
    private _wildOverlay: PIXI.spine.Spine;
    private _prevHitCount: number;
    private _splitX2Sprite: PIXI.Sprite;
    private _animationState: JumpingWildAnimStates = JumpingWildAnimStates.IDLE;
    private _reelPosition: POSITION;
    private _data: ISpecialSymData;
    private _upgraded: boolean = false;
    private _aliasName: string;
    private _multiplier: Multiplier;
    private _upgradedMultiplier: Multiplier;
    private _hitCount: number = 0;
    private _jumpIndex: number = 0;
    private _splitStateVisible: boolean;

    constructor(spineData: PIXI.spine.core.SkeletonData, type: JW_TYPE, aliasName: string) {
        super(spineData);
        this._type = type;
        this._aliasName = aliasName;
        this.init();
        this.position.y -= 8;
    }

    public addWildOverlay(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._wildOverlay = new PIXI.spine.Spine(RoadKill.assetLoader.getSpineAsset("wildOverlay"));
        this._wildOverlay.alpha = 0;
        this._wildOverlay.position.set(0, 6);
        this.addChild(this._wildOverlay);
        this._wildOverlay.state.setAnimation(0, "W_In", false);
        tl.add(gsap.to(this._wildOverlay, {duration: 0.1, alpha: 1}));
        return tl;
    }

    public start(reelId: number, rowId: number): void {
        const pos: PIXI.Point = RoadKillUtils.getSymAbsolutePos(reelId, rowId);
        this.position.set(pos.x, pos.y);
    }

    public onLand(reelID: number, rowId: number, isRestore: boolean, isUpgraded: boolean = false): TimelineLite {
        const pos: PIXI.Point = RoadKillUtils.getSymAbsolutePos(reelID, rowId),
            tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            this.visible = true;
            this._wildOverlay.alpha = 0;
            this.position = pos;
            this.alpha = isRestore ? 1 : 0;
            if (!isRestore) {
                isUpgraded ? this.setSkin(JumpinWildSkins.UPGRADED) : this.setSkin(JumpinWildSkins.NORMAL);
            }
            this._reelPosition = {reelId: reelID, rowId: rowId};
        });
        if (!isRestore) {
            tl.to(this, {alpha: 1, duration: 0.2}, 0);
        }
        tl.add(RKHelper.playSpineWithTimeline(this, 0, JumpingWildAnimStates.TRIGGER), "<");
        tl.add(() => {
            this._wildOverlay.state.setAnimation(0, "W_In", false);
            this._wildOverlay.alpha = 1;
            this.autoUpdate = true;
            this.state.setAnimation(0, JumpingWildAnimStates.IDLE, true);
            this.animationState = JumpingWildAnimStates.IDLE;
        });
        return tl;
    }

    public jumpToPos(dest: POSITION, isStealthMode: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const pos: PIXI.Point = RoadKillUtils.getSymAbsolutePos(dest.reelId, dest.rowId);
        if (isStealthMode) {
            tl.to(this.scale, {duration: 0.1, x: 0, y: 0});
            tl.add(() => {
                this.position.set(pos.x, pos.y);
            });
            tl.to(this.scale, {duration: 0.1, x: 1, y: 1});
        } else {
            tl.to(this, {duration: 0.2, x: pos.x, y: pos.y}, 0);
        }
        tl.add(() => {
            this._reelPosition = dest;
        });
        return tl;
    }

    public playHitImpact(hitFromLeft: boolean): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        tl.add([RKHelper.playSpineWithTimeline(this, 0, hitFromLeft ? this.animationState = JumpingWildAnimStates.HIT_LEFT : this.animationState = JumpingWildAnimStates.HIT_RIGHT), () => {
            if (this.type === JW_TYPE.BEAR) {
                RoadKill.soundPlayer.playTrack(RoadKillSound.BEAR_SHAKE);
            } else if (this.type === JW_TYPE.DEER) {
                RoadKill.soundPlayer.playTrack(RoadKillSound.DEER_SHAKE);
            } else {
                RoadKill.soundPlayer.playTrack(RoadKillSound.RABBIT_SHAKE);
            }
        }]);
        return tl;
    }

    public onHit(isFinalHit: boolean): TimelineLite {
        isFinalHit && this._hitCount++;
        const tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            this.autoUpdate = true;
            this.state.setAnimation(0, JumpingWildAnimStates.IN, true);
            this.animationState = JumpingWildAnimStates.IN;
        });
        if (isFinalHit) {//hitCount adjusted at updateTopAnimalHits if 2 hit in 1 spin
            tl.add(RoadKill.topAnimalController.updateTopAnimalHits(this.aliasName as SYMBOL_NAME, RoadKill.model.mode === RoadKillGameMode.SUPER_FREESPIN ? RoadKill.model.totalAnimalHitsSFS : this.data.getHitCount), 0);
        }
        if (this._hitCount + this._prevHitCount >= 5 && this._data.upgraded) {
            tl.add(this.onUpgrade(false, true), 0);
        }
        return tl;
    }

    public onModeChange(nextMode: RoadKillGameMode): void {
        Logger.logDev("onModeChange called", RoadKill.model.characterData.get(this.type));
        if (RoadKill.model.characterData.get(this.type)?.upgraded) {
            this.onUpgrade(true);
        }
    }

    public onUpgrade(onModeChanged: boolean = false, fromHitState: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite(),
            isRestore: boolean = RoadKill.model.isRestore;
        if (!this._upgraded) {
            if (!isRestore) {
                const topAnimalHead: TopAnimalHead | undefined = RoadKill.topAnimalController.getTopAnimalHeadByAliasName(this.aliasName as SYMBOL_NAME);
                if (topAnimalHead) {
                    tl.add(topAnimalHead.onUpgrade(), 0.2);
                }
                tl.add([
                    RKHelper.playSpineWithTimeline(this, 0, (fromHitState ? JumpingWildAnimStates.UPGRADE_FROM_IN : JumpingWildAnimStates.UPGRADE)),
                    () => {
                        this.setSkin(JumpinWildSkins.UPGRADED);
                    }
                ], 0.5);

                if (!onModeChanged) { //onModeChange the upgraded heart will be coming from last round. so no need to update heart
                    tl.add(RoadKill.lifeView.onAnimalUpgrade());
                }
            } else {
                tl.add(() => {
                    this.setSkin(JumpinWildSkins.UPGRADED);
                })
            }
            switch (this.type) {
                case JW_TYPE.BEAR:
                    tl.add(() => {
                        this.addUpgradedMultiplier(2);
                    });
                    break;
                case JW_TYPE.DEER:
                    break;
                case JW_TYPE.RABBIT:
                    break;
            }
            this._upgraded = true;
        }
        return tl;
    }

    public hide(): void {
        this.visible = false;
        this.reset();
    }

    public addMultiplier(value: number, shouldTransition: boolean = false): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._multiplier = this._multiplier || new Multiplier(RoadKillGameAssets.BITMAP_FONT_NUMBERS_MULTIPLIER, 22, "x");
        if (shouldTransition) {
            this._multiplier.alpha = 0;
            tl.to(this._multiplier, {duration: 0.1, alpha: 1});
        }
        if (!this._multiplier.parent) {
            this.addChild(this._multiplier)
        }
        this._multiplier.value = value;
        return tl;
    }

    public addUpgradedMultiplier(value: number): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        this._upgradedMultiplier = this._upgradedMultiplier || new Multiplier(RoadKillGameAssets.BITMAP_FONT_NUMBERS_MULTIPLIER, 22, "x");
        this._upgradedMultiplier.alpha = 0;
        this._upgradedMultiplier.position.y = 30;
        tl.to(this._upgradedMultiplier, {duration: 0.1, alpha: 1});
        if (!this._upgradedMultiplier.parent) {
            this.addChild(this._upgradedMultiplier)
        }
        this._upgradedMultiplier.value = value;
        return tl;
    }

    public removeMultiplier(): void {
        if (this._multiplier) {
            this._multiplier.value = 0;
            this.removeChild(this._multiplier);
        }
    }

    public playOutAnim(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        if (this.animationState === JumpingWildAnimStates.IN) {
            tl.add(RKHelper.playSpineWithTimeline(this, 0, JumpingWildAnimStates.OUT));
            this.animationState = JumpingWildAnimStates.OUT;
            // tl.add(() => {
            //     this.autoUpdate = true;
            //     this.state.setAnimation(0, JumpingWildAnimStates.IDLE, true);
            //     this.animationState = JumpingWildAnimStates.IDLE;
            // })
        }
        return tl;
    }

    public removeUpgradedMultiplier(): void {
        if (this._upgradedMultiplier) {
            this._upgradedMultiplier.value = 0;
            this.removeChild(this._upgradedMultiplier);
        }
    }

    public setSkin(skinName: string): void {
        this.skeleton.setSkinByName(skinName);
    }

    public showX2Split(show: boolean): TimelineLite {
        this._splitStateVisible = show;
        const tl: TimelineLite = new TimelineLite();
        tl.to(this._splitX2Sprite, {alpha: show ? 1 : 0, duration: 0.1}, 0);
        return tl;
    }

    private createX2Sprite(): PIXI.Sprite {
        const sprite: PIXI.Sprite = new PIXI.Sprite(RoadKill.assetLoader.getTextures("x2")[0]);
        sprite.scale.set(0.8);
        sprite.position.set(30, -43);
        sprite.anchor.set(0.5);
        sprite.alpha = 0;
        sprite.visible = true;
        sprite.name = "x2Split";
        this.addChild(sprite);
        return sprite;
    }

    private reset(): void {
        this._upgraded = false;
        this.hitCount = 0;
        this._prevHitCount = 0;
        this.removeMultiplier();
        this._upgradedMultiplier && this.removeUpgradedMultiplier();
    }

    private init(): void {
        this.name = "JW_" + this.type;
        this._splitX2Sprite = this.createX2Sprite();
        this.addWildOverlay();
        this.animationState = JumpingWildAnimStates.IDLE;
        this.setSkin(JumpinWildSkins.NORMAL);
        this.state.setAnimation(0, "idle", false);
        this.hide();
        this.pivot.y = -11;
    }

    get animationState(): JumpingWildAnimStates {
        return this._animationState;
    }

    set animationState(animationName: JumpingWildAnimStates) {
        this._animationState = animationName;
    }

    get reelPosition(): POSITION {
        return this._reelPosition;
    }

    get data(): ISpecialSymData {
        return this._data;
    }

    set data(value: ISpecialSymData) {
        this._prevHitCount = this._data?.getHitCount || 0;
        this._data = value;
        this._hitCount = 0;
    }

    get upgraded(): boolean {
        return this._upgraded;
    }

    set upgraded(value: boolean) {
        this._upgraded = value;
    }

    get aliasName(): string {
        return this._aliasName;
    }

    get multiplier(): Multiplier {
        return this._multiplier;
    }

    get upgradedMultiplier(): Multiplier {
        return this._upgradedMultiplier;
    }

    get hitCount(): number {
        return this._hitCount;
    }

    set hitCount(value: number) {
        this._hitCount = value;
    }

    get jumpIndex(): number {
        return this._jumpIndex;
    }

    set jumpIndex(value: number) {
        this._jumpIndex = value;
    }

    get splitStateVisible(): boolean {
        return this._splitStateVisible;
    }

    get type(): JW_TYPE {
        return this._type;
    }
}